import { useEffect, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, FormControlLabel, TextField, makeStyles, Box, Fab, Switch } from "@material-ui/core";
import { Autocomplete, createFilterOptions } from '@material-ui/lab';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import SaveIcon from '@material-ui/icons/Save';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Http from '../../engine/Http';
import { Dimension, Price, getDimensionsDisplayText } from '../../model/Items';
import { BonnellSpring } from '../bonnellspring/BonnellSpringDetails';
import { formatSpringName } from '../../model/Springs';
import { PocketSpring } from '../../api/OrderSpringApi';
import { roundCurrency } from '../../engine/PriceUtilities';
import { PricedSpring } from '../../api/PricedSpringApi';
import useAuthenticationHeader from '../../security/AuthenticationHeader.hook';

type OrderItemTransformation = (orderItem: OrderItem) => OrderItem;

interface Props {
  readonly customerId: number;
  readonly priceListId: number;
  orderId: number|null,
  orderItemType: 'form'|'shape'|'kit'|'bonnell-spring'|'pocket-spring';
  orderItemElementName?: 'bonnellSpring'|'pocketSpring';
  title: string,
  promptMessage: string;
  modificationDisabled: boolean;
  filteringByDimensionEnabled: boolean;
};

interface Material {
  name: string;
};

interface Item {
  id: number;
  name: string;
  unitPrice: string;
  material?: Material;
  dimensions?: Dimension;
  price?: Price|null;
};

interface OrderItem {
  id: number;
  item: Item;
  quantityOrdered: number;
  quantityDelivered: number;
  unitPrice: string;
};

const useAccordionStyles = makeStyles(theme => ({
  root: {
    marginTop: 10,
    minWidth: 600
  },
}));

const useOrderItemStyles = makeStyles(theme => ({
  root: {
    marginTop: 10,
    marginRight: 0
  },
  label: {
    width: 360,
    margin: theme.spacing(1)
  },
}));

const useStyles = makeStyles(theme => ({
  itemSelect: {
    width: 360,
    margin: theme.spacing(2)
  },
  dataInput: {
    width: 80
  },
  orderItemButton: {
    marginTop: theme.spacing(1),
    marginLeft: theme.spacing(1)
  },
  filterOnlyDimemsionsSwitch: {
    paddingBottom: theme.spacing(2),
    paddingLeft: theme.spacing(8),
    marginTop: theme.spacing(-0.5),
  }
}));

const formatOrderBonnellSpringName = (bonnellSpring: BonnellSpring) => {
  console.info(`## formatBonnellSpringName: ${JSON.stringify(bonnellSpring)}`);
  return formatSpringName(bonnellSpring.name, bonnellSpring.width, bonnellSpring.length, [bonnellSpring.material.thickness]);
};

const formatOrderPocketSpringName = (pocketSpring: PocketSpring) => {
  console.info(`## formatOrderPocketSpringName: ${JSON.stringify(pocketSpring)}`);
  const zones = pocketSpring.zones;
  const length = zones
    .map(zone => zone.length)
    .reduce((zoneLength, anotherZoneLenth) => zoneLength + anotherZoneLenth, 0);
  const thicknesses = zones.map(zone => zone.material.thickness);
  return formatSpringName(pocketSpring.name, pocketSpring.width, length, thicknesses);
};

const formatPricedSpringName = (pricedSpring: PricedSpring) => {
  console.info(`## formatPricedSpringName: ${JSON.stringify(pricedSpring)}`);
  return formatSpringName(
    pricedSpring.name,
    pricedSpring.width,
    pricedSpring.length,
    pricedSpring.materials.map(material => material.thickness));
};

const OrderItemsAccordion = (props: Props) => {
  const classes = useStyles();
  const orderItemClasses = useOrderItemStyles();
  const accordionClasses = useAccordionStyles();
  const authenticationHeader = useAuthenticationHeader();
  const priceListId = props.priceListId;
  const orderItemType = props.orderItemType;
  const orderItemPropertyName = props.orderItemElementName || props.orderItemType;
  const orderItemsPath = `/api/customers/${props.customerId}/orders/${props.orderId}/${orderItemType}s`;
  const [selectedItem, setSelectedItem] = useState<Item|null>(null);
  const [orderedQuantity, setOrderedQuantity] = useState<number>(0);
  const [deliveredQuantity, setDeliveredQuantity] = useState<number|null>(null);
  const [unitPrice, setUnitPrice] = useState<string|null>(null);
  const [referenceItems, setReferenceItems] = useState<Item[]>([]);
  const [selectableItems, setSelectableItems] = useState<any[]>([]);
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  const [fetchedOrderItems, setFetchedOrderItems] = useState<OrderItem[]>([]);
  const [expanded, setExpanded] = useState<boolean|null>(null);
  const [filterOnlyDimensions, setFilterOnlyDimensions] = useState<boolean>(false);

  const handleAccordionChanged = (expanded: boolean) => {
    setExpanded(expanded);
  };

  const handleSelectedItemChange = (selectedItem: Item|null) => {
    setSelectedItem(selectedItem);
    setUnitPrice(selectedItem === null ? null : (selectedItem.unitPrice || ''));
  };

  const getFormOrShapeEffectivePrice = (price: any) => {
    if (price === undefined) {
      console.info(`## Price not found!`);
      return null;
    }
    return price.effective;
  };

  const calculateKitTotalPrice = (kitData: any) => kitData.elementsPrice + kitData.packagingPrice;

  const convertToOrderItemElementItem = (itemData: any): Item => { 
    const name
      = orderItemType === 'bonnell-spring' ? formatOrderBonnellSpringName(itemData)
      : orderItemType === 'pocket-spring' ? formatOrderPocketSpringName(itemData)
      : itemData.name;
    return {
      id: itemData.id,
      name,
      unitPrice: "",
      material: itemData.material,
      dimensions: itemData.dimension || itemData.block,
      price: itemData.price || itemData.unitPrice
    }
  };

  const convertToPricedInventoryItem = (itemData: any): Item => {
    const price
      = orderItemType === 'form' ? itemData.price
      : orderItemType === 'shape' ? itemData.unitPrice
      : null;
    const unitPrice
      = price !== null ? getFormOrShapeEffectivePrice(price)
      : orderItemType === 'kit' ? calculateKitTotalPrice(itemData)
      : orderItemType === 'bonnell-spring' ? (itemData.price === undefined ? undefined : roundCurrency(itemData.price))
      : orderItemType === 'pocket-spring' ? (itemData.price === undefined ? undefined : roundCurrency(itemData.price))
      : itemData.price;
    const name
      = orderItemType === 'bonnell-spring' ? formatPricedSpringName(itemData)
      : orderItemType === 'pocket-spring' ? formatPricedSpringName(itemData)
      : itemData.name
    return {
      id: itemData.id,
      name,
      unitPrice,
      material: itemData.material,
      dimensions: itemData.dimension || itemData.block,
      price
    }
  };

  const convertToOrderItem = (orderItemData: any): OrderItem => ({
    id: orderItemData.id,
    item: convertToOrderItemElementItem(orderItemData[orderItemPropertyName]),
    quantityOrdered: orderItemData.quantityOrdered,
    quantityDelivered: orderItemData.quantityDelivered,
    unitPrice: orderItemData.unitPrice
  });

  const fetchSelectableItems = () => Http
    .get(`/api/customers/${props.customerId}/price-lists/${priceListId}/${orderItemType}s`, [ authenticationHeader ])
    .then(data => {
      console.debug(`Item data fetched: ${JSON.stringify(data)}`);
      const items = (data as Item[])
        .map(convertToPricedInventoryItem);
      setReferenceItems(items);
      setSelectableItems(items.filter(
        item => !orderItems.map(orderItem => orderItem.item.id).includes(item.id)));
    })
    .catch(reason => console.error(`Could not fetch items data: ${reason}`));

  const fetchOrderItems = () => Http
    .get(orderItemsPath, [ authenticationHeader ])
    .then(data => {
      console.debug(`Order items fetched: ${JSON.stringify(data)}`);
      const orderItems: OrderItem[] = (data as any[]).map(convertToOrderItem);
      setFetchedOrderItems(orderItems);
      const editableOrderItems = orderItems
        .sort((orderItem, anotherOrderItem) => orderItem.id - anotherOrderItem.id);
      setOrderItems(editableOrderItems);
    })
    .catch(reason => console.error(`Could not fetch order items data: ${reason}`));

  const findOrderItem = (orderItems: OrderItem[], itemId: number) =>
    orderItems.find(orderItem => orderItem.item.id === itemId);

  const handleOrderItemOrderedQuantityChange = (itemId: number, orderedQuantity: string) =>
    handleOrderItemPropertyChange(
      itemId,
      orderItem => ({ ...orderItem, quantityOrdered: Number(orderedQuantity)}));

  const handleOrderItemDeliveredQuantityChange = (itemId: number, deliveredQuantity: string) =>
    handleOrderItemPropertyChange(
      itemId,
      orderItem => ({ ...orderItem, quantityDelivered: Number(deliveredQuantity)}));

  const handleOrderItemUnitPriceChange = (itemId: number, unitPrice: string) =>
    handleOrderItemPropertyChange(
      itemId,
      orderItem => ({ ...orderItem, unitPrice: unitPrice}));
  
  const handleOrderItemPropertyChange = (itemId: number, orderItemTransformation: OrderItemTransformation) =>
    setOrderItems(orderItems.map(orderItem => orderItem.item.id === itemId
      ? orderItemTransformation(orderItem)
      : orderItem));

  const handleSelectedUnitPriceChange = (unitPrice: string) => setUnitPrice(unitPrice);

  const handleSelectedOrderedQuantityChange = (quantity: string) => setOrderedQuantity(Number(quantity));

  const handleSelectedDeliveredQuantityChange = (quantity: string) => setDeliveredQuantity(Number(quantity));

  const clearSelectedValues = () => {
    setSelectedItem(null);
    setOrderedQuantity(0);
  };

  const addOrderItem = (orderItem: any) => Http
    .post(orderItemsPath, orderItem, [ authenticationHeader ])
    .then(fetchOrderItems)
    .then(clearSelectedValues)
    .catch(reason => console.error(`Could not add order item: ${reason}`));

  const getSelectedOrderItem = () =>
    (selectedItem && orderedQuantity && unitPrice)
      ? ({
        [orderItemPropertyName]: { id: selectedItem.id },
        quantityOrdered: orderedQuantity,
        quantityDelivered: deliveredQuantity,
        unitPrice
      })
      : null;

  const handleAddOrderItem = () => {
    const orderItem = getSelectedOrderItem(); 
    if (orderItem) {
      console.debug(`Adding new order item: ${JSON.stringify(orderItem)}`);
      addOrderItem(orderItem);
    } else {
      console.debug('No item selected');
    }
  };

  const createOrderItemUpdateData = (orderItem: OrderItem) => ({
    quantityOrdered: orderItem.quantityOrdered,
    quantityDelivered: orderItem.quantityDelivered,
    unitPrice: orderItem.unitPrice
  });

  const handleUpdateOrderItem = (orderItem: OrderItem) => Http
    .put(`${orderItemsPath}/${orderItem.item.id}`, createOrderItemUpdateData(orderItem), [ authenticationHeader ])
    .then(fetchOrderItems)
    .then(clearSelectedValues)
    .catch(reason => console.error(`Could not update order item: ${reason}`));

  const handleDeleteOrderItem = (deletedOrderItem: OrderItem) => Http
    .delete(`${orderItemsPath}/${deletedOrderItem.item.id}`, [ authenticationHeader ])
    .then(fetchOrderItems)
    .then(clearSelectedValues)
    .catch(reason => console.error(`Could not delete order item: ${reason}`));

  const isAddOrderItemDisabled = () => !selectedItem || orderedQuantity <= 0;

  const isUpdateOrderItemDisabled = (orderItemData: OrderItem) =>
    orderItemData.quantityOrdered <= 0
    || orderItemData.quantityDelivered <= 0
    || Number(orderItemData.unitPrice) <= 0
    || !isAnyOrderItemPropertyChanged(orderItemData);

  const isAnyOrderItemPropertyChanged = (orderItemData: OrderItem) => {
    const fetchedOrderItem = findOrderItem(fetchedOrderItems, orderItemData.item.id);
    return fetchedOrderItem !== undefined
      ? (fetchedOrderItem.quantityOrdered !== orderItemData.quantityOrdered
        || fetchedOrderItem.quantityDelivered !== orderItemData.quantityDelivered
        || fetchedOrderItem.unitPrice !== orderItemData.unitPrice)
      : false;
  };

  const surroundWithParentheses = (value: string) : string => `(${value})`;

  const getItemPriceAdjustmentDiplayedText = (price?: Price|null) : string =>
    price === undefined || price === null ? '' : ` <+${price.adjustment || 0}%>`;

  const getItemDimensionDisplayedText = (dimension?: Dimension) : string =>
    dimension === undefined ? '' : ` [${getDimensionsDisplayText(dimension)}]`;

  const getSelectableItemDisplayedText = (item: Item) : string => getItemDisplayedText(item);

  const findReferenceItem = (id: number) : Item|undefined => referenceItems.find(item => item.id === id);

  const getOrderItemDisplayedText = (item: Item) : string => {
    const referenceItem = findReferenceItem(item.id);
    return getItemDisplayedText({
      ...item,
      dimensions: referenceItem?.dimensions,
      price: referenceItem?.price || null
    });
  }

  const getItemDisplayedText = (item: Item) => {
    const itemName = item.name;
    const materiaDisplayedText = `${item.material ? (' ' + surroundWithParentheses(item.material.name)) : ''}`;
    const dimensionDisplayedText = getItemDimensionDisplayedText(item.dimensions);
    const priceAdjustmentDiplayedText = getItemPriceAdjustmentDiplayedText(item.price)
    return `${itemName}${materiaDisplayedText}${dimensionDisplayedText}${priceAdjustmentDiplayedText}`;
  }

  const renderOrderItem = (orderItem: OrderItem) => {
    const item = orderItem.item;
    const currentOrderItemData = findOrderItem(orderItems, item.id);
    const unitPrice = currentOrderItemData?.unitPrice;
    const quantityOrdered = currentOrderItemData?.quantityOrdered;
    const quantityDelivered = currentOrderItemData?.quantityDelivered;
    return (
      <Box key={ item.id } display='flex' flexDirection='row'>
        <FormControlLabel
          classes={ orderItemClasses }
          label={ getOrderItemDisplayedText(item) }
          control={
          <>
            <TextField
              label='wykonano'
              InputLabelProps={{ shrink: true }}
              type='number'
              variant='outlined'
              className={ classes.dataInput }
              onChange={ event => handleOrderItemDeliveredQuantityChange(item.id, event.target.value) }
              value={ quantityDelivered }
              disabled={ props.modificationDisabled }
            />
            <TextField
              label='zamówiono'
              InputLabelProps={{ shrink: true }}
              type='number'
              variant='outlined'
              className={ classes.dataInput }
              onChange={ event => handleOrderItemOrderedQuantityChange(item.id, event.target.value) }
              value={ quantityOrdered }
              disabled={ props.modificationDisabled }
            />
            <TextField
              label='cena'
              InputLabelProps={{ shrink: true }}
              variant='outlined'
              className={ classes.dataInput }
              onChange={ event => handleOrderItemUnitPriceChange(item.id, event.target.value) }
              value={ unitPrice }
              disabled={ props.modificationDisabled }
            />
          </>}
          labelPlacement='start'
        />
        <Fab
          className={ classes.orderItemButton }
          aria-label='update'
          color='primary'
          onClick={ () => handleUpdateOrderItem(orderItem) }
          size='small'
          disabled={ props.modificationDisabled || isUpdateOrderItemDisabled(orderItem) }
        >
          <SaveIcon />
        </Fab>
        <Fab
          className={ classes.orderItemButton }
          aria-label='delete'
          color='secondary'
          onClick={ () => handleDeleteOrderItem(orderItem) }
          size='small'
          disabled={ props.modificationDisabled }
        >
          <DeleteIcon />
        </Fab>
      </Box>
    );
  };

  const filterOptions = createFilterOptions({
    stringify: (option: Item) : string => {
      const dimensions = option.dimensions;
      return dimensions === undefined ? '' : getDimensionsDisplayText(dimensions);
    }
  });

  const renderOrderItemSelectionBox = () =>
    <Box display='flex' flexDirection='row'>
      <Autocomplete
          value={ selectedItem }
          onChange={ (event, item) => handleSelectedItemChange(item) }
          className={ classes.itemSelect }
          options={ selectableItems }
          getOptionLabel={ getSelectableItemDisplayedText }
          renderInput={ (params) => <TextField {...params} /> }
          filterOptions={ filterOnlyDimensions ? filterOptions : undefined }
      />
      <TextField
        label='cena'
        InputLabelProps={{ shrink: true }}
        variant='outlined'
        className={classes.dataInput}
        onChange={event => handleSelectedUnitPriceChange(event.target.value)}
        value={unitPrice}
      />
      <TextField
        label='zamówiono'
        InputLabelProps={{ shrink: true }}
        type='number'
        variant='outlined'
        className={classes.dataInput}
        onChange={event => handleSelectedOrderedQuantityChange(event.target.value)}
        value={orderedQuantity}
      />
      <TextField
        label='wykonano'
        InputLabelProps={{ shrink: true }}
        type='number'
        variant='outlined'
        className={classes.dataInput}
        onChange={event => handleSelectedDeliveredQuantityChange(event.target.value)}
        value={deliveredQuantity}
      />
      <Fab
        className={classes.orderItemButton}
        aria-label='add'
        color='primary'
        onClick={handleAddOrderItem}
        size='small'
        disabled={isAddOrderItemDisabled()}
      >
        <AddIcon />
      </Fab>
    </Box>
  ;

  const renderOrderItems = () => orderItems.map(renderOrderItem);
  
  const handleOnlyDimensionsChanged
    = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => setFilterOnlyDimensions(checked);

  const renderOrderItemSelectionCaption = () : JSX.Element =>
    <Box display='flex' flexDirection='row'>
      { props.promptMessage }
      { props.filteringByDimensionEnabled === true && <FormControlLabel
        className={ classes.filterOnlyDimemsionsSwitch }
        label="Filtruj tylko według wymiarów"
        control={
          <Switch
            color='primary'
            defaultChecked
            checked={ filterOnlyDimensions }
            onChange={ handleOnlyDimensionsChanged }
            size='small'
          />}
      /> }
    </Box>
  ;

  useEffect(() => {
    if (expanded === null || expanded === true) {
      if (props.orderId) {
        fetchOrderItems();
      } 
    }
  }, [expanded]);

  useEffect(() => {
    fetchSelectableItems();
  }, [orderItems, priceListId]);

  return (
    <Accordion classes={accordionClasses} onChange={(event, expanded) => handleAccordionChanged(expanded)}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        { props.title }
      </AccordionSummary>
      <AccordionDetails>
        <Box display='flex' flexDirection='column' padding={0} margin={0}>
          { !props.modificationDisabled && selectableItems.length > 0 && renderOrderItemSelectionCaption() }
          { !props.modificationDisabled && selectableItems.length > 0 && renderOrderItemSelectionBox() }
          { renderOrderItems() }
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};
export default OrderItemsAccordion;